import { useEffect, useState } from 'react';

import type { Account, AccountChanges } from '../../api/monitor';
import { accountTitle, usesOtp } from '../../lib/accounts';

interface EditAccountModalProps {
  account: Account;
  saving: boolean;
  onClose: () => void;
  onSubmit: (changes: AccountChanges) => Promise<void>;
}

/**
 * Edição de conta: trocar a senha do portal (e, na Claro, a caixa e o token do
 * OTP) e ativar/inativar.
 *
 * O login não é editável — é a chave da conta na operadora. A senha e o token
 * nunca chegam à tela, então os campos começam vazios e vazio quer dizer "não
 * mexer". Inativar tira a conta das rotinas diárias sem apagar nada: é como se
 * aposenta conta de teste ou de senha vencida.
 */
export function EditAccountModal({
  account,
  saving,
  onClose,
  onSubmit,
}: EditAccountModalProps) {
  const otp = usesOtp(account.operator);
  const [password, setPassword] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpAppPassword, setOtpAppPassword] = useState('');
  const [active, setActive] = useState(account.active);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = async () => {
    const changes: AccountChanges = {};

    if (password.trim()) changes.password = password;
    if (otp && otpEmail.trim()) changes.otpEmail = otpEmail.trim();
    if (otp && otpAppPassword.trim())
      changes.otpAppPassword = otpAppPassword.trim();
    if (active !== account.active) changes.active = active;

    if (!Object.keys(changes).length) {
      setError('Nada para alterar.');
      return;
    }

    setError(null);

    try {
      await onSubmit(changes);
    } catch (cause) {
      setError((cause as Error).message);
    }
  };

  const input =
    'border-line-300 focus:border-navy-800 rounded-[10px] border p-3 text-[14px] outline-none focus:ring-2 focus:ring-[#00244733]';

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-50 bg-[rgb(10_20_45/0.45)]"
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Editar conta ${accountTitle(account)}`}
        className="animate-rise-fast fixed top-1/2 left-1/2 z-[51] max-h-[85dvh] w-[min(480px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-[18px] bg-white p-[22px]"
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="text-[16px] font-extrabold">
            Editar conta — {account.operator.name}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-400 hover:text-ink-900 p-1 text-[20px] leading-none"
          >
            ×<span className="sr-only">Fechar</span>
          </button>
        </div>

        <div className="text-ink-600 mb-3.5 text-[12px]">
          Login <b className="font-mono">{accountTitle(account)}</b>. Deixe em
          branco o que não for trocar.
        </div>

        <div className="flex flex-col gap-2.5">
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={`Nova senha do portal ${account.operator.name}`}
            aria-label="Nova senha do portal"
            autoComplete="new-password"
            className={input}
          />

          {otp ? (
            <>
              <input
                type="email"
                value={otpEmail}
                onChange={(event) => setOtpEmail(event.target.value)}
                placeholder={
                  account.otpEmail
                    ? `E-mail do OTP (atual: ${account.otpEmail})`
                    : 'E-mail Gmail (recebe o OTP)'
                }
                aria-label="E-mail do OTP"
                autoComplete="off"
                className={input}
              />
              <input
                type="text"
                value={otpAppPassword}
                onChange={(event) => setOtpAppPassword(event.target.value)}
                placeholder={
                  account.hasOtpAppPassword
                    ? 'Novo token de app do Google (já há um salvo)'
                    : 'Token de app do Google'
                }
                aria-label="Token de app do Google"
                autoComplete="off"
                className={`${input} font-mono`}
              />
            </>
          ) : null}

          <label className="border-line-300 flex cursor-pointer items-center justify-between gap-3 rounded-[10px] border px-3 py-2.5">
            <span>
              <span className="block text-[13.5px] font-bold">Conta ativa</span>
              <span className="text-ink-600 block text-[11.5px]">
                Inativa, a conta sai da leitura diária do portal. Nada é
                apagado.
              </span>
            </span>
            <input
              type="checkbox"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
              className="size-4 shrink-0"
            />
          </label>
        </div>

        {error ? (
          <div className="text-danger-600 mt-2.5 text-[12.5px] font-bold">
            {error}
          </div>
        ) : null}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="border-line-300 text-ink-600 flex-1 rounded-[10px] border bg-white p-3 text-[13.5px] font-extrabold"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="bg-navy-800 hover:bg-navy-950 flex-1 rounded-[10px] p-3 text-[13.5px] font-extrabold text-white disabled:opacity-60"
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </>
  );
}
